/**
 * 図書館フィルタ用の Zustand ストア (サーバ保存)
 *
 * `selectedLibraryIds` が空配列のときは「フィルタなし」= 全館を表示。
 * 1件以上選択されているときは、その館の所蔵のみを表示する。
 *
 * サーバ側 `/library-selections` と PUT で丸ごと置換して同期する。
 * `toggle` / `setMany` / `clear` は楽観的更新で先に state を書き換え、
 * サーバへの反映が失敗した場合はロールバックする。
 */

import { create } from 'zustand'

import { CLIENT_ID } from '@/api/client'
import { getLibrarySelections, putLibrarySelections } from '@/api/services/library-selection'
import { registerSyncListener } from '@/api/sync-events'

type LibraryFilterState = {
  /** 選択された図書館 ID の配列 (空 = フィルタ無効) */
  selectedLibraryIds: number[]
  /** 一時的にフィルタを無視して全館を表示する (サーバには保存しない) */
  searchAllLibraries: boolean
  loaded: boolean
  loading: boolean
  error: string | null

  isEnabled: () => boolean
  isSelected: (libraryId: number) => boolean
  setSearchAllLibraries: (value: boolean) => void
  loadFilter: () => Promise<void>
  toggle: (libraryId: number) => Promise<void>
  setMany: (libraryIds: number[]) => Promise<void>
  clear: () => Promise<void>
}

/** 選択集合をサーバへ PUT し、失敗したら prev で state を復元する */
const persistToServer = async (
  next: number[],
  prev: number[],
  set: (partial: Partial<LibraryFilterState>) => void,
): Promise<void> => {
  const unique = Array.from(new Set(next)).sort((a, b) => a - b)
  set({ selectedLibraryIds: unique, error: null })
  try {
    const saved = await putLibrarySelections(unique)
    set({ selectedLibraryIds: saved })
  } catch (e) {
    set({
      selectedLibraryIds: prev,
      error: e instanceof Error ? e.message : '図書館フィルタの保存に失敗しました',
    })
  }
}

export const useLibraryFilterStore = create<LibraryFilterState>()((set, get) => ({
  selectedLibraryIds: [],
  searchAllLibraries: false,
  loaded: false,
  loading: false,
  error: null,

  isEnabled: () => get().selectedLibraryIds.length > 0,
  isSelected: (libraryId) => get().selectedLibraryIds.includes(libraryId),
  setSearchAllLibraries: (value) => set({ searchAllLibraries: value }),

  loadFilter: async () => {
    if (get().loading) return
    set({ loading: true, error: null })
    try {
      const ids = await getLibrarySelections()
      set({ selectedLibraryIds: ids, loaded: true, loading: false })
    } catch (e) {
      const message = e instanceof Error ? e.message : '図書館フィルタの取得に失敗しました'
      set({ loading: false, error: message })
    }
  },

  toggle: async (libraryId) => {
    const prev = get().selectedLibraryIds
    const next = prev.includes(libraryId) ? prev.filter((id) => id !== libraryId) : [...prev, libraryId]
    await persistToServer(next, prev, set)
  },

  setMany: async (libraryIds) => {
    const prev = get().selectedLibraryIds
    await persistToServer(libraryIds, prev, set)
  },

  clear: async () => {
    const prev = get().selectedLibraryIds
    if (prev.length === 0) return
    await persistToServer([], prev, set)
  },
}))

// ============================================================================
// SSE リモートイベント購読
// ============================================================================
// 他端末で図書館フィルタが更新されたら再読み込みする。
registerSyncListener((event) => {
  if (event.type !== 'library_selection.updated') return
  if (event.origin === CLIENT_ID) return

  // サーバから現在の全選択を再取得する。
  void useLibraryFilterStore.getState().loadFilter()
})
